import { useState, useEffect, useCallback, useRef } from 'react';
import { safeGetJSON, safeSetJSON } from '@/lib/safeStorage';

interface EasterEggState {
  discovered: string[];
  lastDiscovery: string | null;
  konamiActivated: boolean;
  longPressDiscovered: boolean;
  secretSequenceDiscovered: boolean;
  tripleClickDiscovered: boolean;
  silenceDiscovered: boolean;
}

const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
const SECRET_SEQUENCE = ['t', 'r', 'u', 't', 'h'];

const getDefaultState = (): EasterEggState => ({
  discovered: [],
  lastDiscovery: null,
  konamiActivated: false,
  longPressDiscovered: false,
  secretSequenceDiscovered: false,
  tripleClickDiscovered: false,
  silenceDiscovered: false,
});

export const useEasterEggs = () => {
  const [state, setState] = useState<EasterEggState>(() => 
    safeGetJSON('easter_eggs', getDefaultState())
  );
  
  const konamiIndex = useRef(0);
  const sequenceIndex = useRef(0);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const clickCount = useRef(0);
  const clickTimer = useRef<NodeJS.Timeout | null>(null);
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);

  // Save state changes
  useEffect(() => {
    safeSetJSON('easter_eggs', state);
  }, [state]);

  // Discover an easter egg
  const discover = useCallback((id: string) => {
    setState(prev => {
      if (prev.discovered.includes(id)) return prev;
      
      return {
        ...prev,
        discovered: [...prev.discovered, id],
        lastDiscovery: id,
      };
    });
  }, []);

  // Konami code detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === KONAMI_CODE[konamiIndex.current]) {
        konamiIndex.current++;
        if (konamiIndex.current === KONAMI_CODE.length) {
          setState(prev => ({ ...prev, konamiActivated: true }));
          discover('konami');
          konamiIndex.current = 0;
        }
      } else {
        konamiIndex.current = 0;
      }
      
      // Secret sequence detection
      if (e.key.toLowerCase() === SECRET_SEQUENCE[sequenceIndex.current]) {
        sequenceIndex.current++;
        if (sequenceIndex.current === SECRET_SEQUENCE.length) {
          setState(prev => ({ ...prev, secretSequenceDiscovered: true }));
          discover('secret_sequence');
          sequenceIndex.current = 0;
        }
      } else {
        sequenceIndex.current = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [discover]);

  // Long press detection (3 seconds)
  const startLongPress = useCallback(() => {
    longPressTimer.current = setTimeout(() => {
      if (!state.longPressDiscovered) {
        setState(prev => ({ ...prev, longPressDiscovered: true }));
        discover('long_press');
      }
    }, 3000);
  }, [discover, state.longPressDiscovered]);

  const endLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  // Triple click detection
  const handleClick = useCallback(() => {
    clickCount.current++;
    
    if (clickTimer.current) clearTimeout(clickTimer.current);
    
    clickTimer.current = setTimeout(() => {
      if (clickCount.current >= 3 && !state.tripleClickDiscovered) {
        setState(prev => ({ ...prev, tripleClickDiscovered: true }));
        discover('triple_click');
      }
      clickCount.current = 0;
    }, 500);
  }, [discover, state.tripleClickDiscovered]);

  // Silence detection (2 minutes of no interaction)
  useEffect(() => {
    const resetSilenceTimer = () => {
      if (silenceTimer.current) clearTimeout(silenceTimer.current);
      
      silenceTimer.current = setTimeout(() => {
        if (!state.silenceDiscovered) {
          setState(prev => ({ ...prev, silenceDiscovered: true }));
          discover('silence');
        }
      }, 120000); // 2 minutes
    };

    resetSilenceTimer();

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, resetSilenceTimer));

    return () => {
      if (silenceTimer.current) clearTimeout(silenceTimer.current);
      events.forEach(event => window.removeEventListener(event, resetSilenceTimer));
    };
  }, [discover, state.silenceDiscovered]);

  // Get discovery message
  const getDiscoveryMessage = useCallback((id: string): string => {
    const messages: Record<string, string> = {
      konami: 'You know the old ways.',
      secret_sequence: 'You spelled the truth.',
      long_press: 'Patience reveals secrets.',
      triple_click: 'Persistence pays off.',
      silence: 'Stillness speaks.',
    };
    return messages[id] || 'Something was discovered.';
  }, []);

  return {
    state,
    discover,
    startLongPress,
    endLongPress,
    handleClick,
    getDiscoveryMessage,
    totalDiscovered: state.discovered.length,
  };
};
